import { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Paperclip,
  RefreshCw,
} from "lucide-react";

import {
  getAttachmentDownloadUrl,
  getRelatedContactAttachments,
} from "../../lib/api";
import type {
  ContactAttachment,
  RelatedContactAttachmentGroup,
} from "../../lib/types";

const styles: Record<string, React.CSSProperties> = {
  panel: {
    width: "320px",
    flex: "0 0 320px",
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid #e4e7eb",
    background: "#fafbfc",
    overflow: "hidden",
    transition: "width 150ms ease, flex-basis 150ms ease",
  },
  collapsedPanel: {
    width: "44px",
    flexBasis: "44px",
    alignItems: "center",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "48px",
    padding: "0 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#111827",
    fontSize: "14px",
    fontWeight: 600,
    flexShrink: 0,
  },
  heading: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  countBadge: {
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    background: "#e8eef7",
    color: "#42526e",
    fontSize: "11px",
    fontWeight: 700,
  },
  iconButton: {
    width: "28px",
    height: "28px",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: "6px",
    background: "transparent",
    color: "#5f6b7a",
    cursor: "pointer",
  },
  collapsedContent: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    paddingTop: "12px",
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  group: {
    padding: "14px 12px",
    borderBottom: "1px solid #eef0f2",
  },
  groupTitle: {
    marginBottom: "10px",
    color: "#5f6b7a",
    fontSize: "12px",
    fontWeight: 600,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px",
    border: "1px solid #e3e6e8",
    borderRadius: "7px",
    background: "#ffffff",
  },
  extensionBadge: {
    width: "34px",
    height: "34px",
    flex: "0 0 34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5px",
    background: "#eef3f8",
    color: "#52677d",
    fontSize: "9px",
    fontWeight: 800,
    letterSpacing: "0.3px",
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    color: "#111827",
    fontSize: "13px",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metadata: {
    marginTop: "3px",
    color: "#6b7280",
    fontSize: "11px",
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "5px 7px",
    border: "none",
    borderRadius: "5px",
    background: "transparent",
    color: "#0972d3",
    fontSize: "11px",
    fontWeight: 600,
    cursor: "pointer",
  },
  state: {
    display: "flex",
    minHeight: "160px",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "10px",
    padding: "24px 18px",
    color: "#6b7280",
    textAlign: "center",
    fontSize: "13px",
  },
  error: {
    color: "#b91c1c",
  },
};

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "Size unavailable";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string | null): string {
  if (!value) return "Related chat";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Related chat";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getFileExtension(attachment: ContactAttachment): string {
  const filenameMatch = attachment.filename.match(/\.([^.]+)$/);
  if (filenameMatch?.[1]) return filenameMatch[1].slice(0, 4).toUpperCase();

  const contentTypePart = attachment.contentType.split("/").pop();
  return contentTypePart ? contentTypePart.slice(0, 4).toUpperCase() : "FILE";
}

function AttachmentRow({
  attachment,
}: {
  attachment: ContactAttachment;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const result = await getAttachmentDownloadUrl(attachment.attachmentId);
      const anchor = document.createElement("a");
      anchor.href = result.downloadUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.click();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }, [attachment.attachmentId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      <div style={styles.item}>
        <div style={styles.extensionBadge} aria-hidden>
          {getFileExtension(attachment)}
        </div>
        <div style={styles.itemInfo}>
          <div style={styles.filename} title={attachment.filename}>
            {attachment.filename}
          </div>
          <div style={styles.metadata}>
            {attachment.contentType} · {formatBytes(attachment.size)}
          </div>
        </div>
        <button
          type="button"
          style={{
            ...styles.button,
            opacity: downloading ? 0.65 : 1,
            cursor: downloading ? "wait" : "pointer",
          }}
          disabled={downloading}
          onClick={download}
          aria-label={`Download ${attachment.filename}`}
        >
          <Download aria-hidden size={14} />
          {downloading ? "Preparing…" : "Download"}
        </button>
      </div>
      {error ? <div style={{ ...styles.metadata, ...styles.error }}>{error}</div> : null}
    </div>
  );
}

function AttachmentGroup({
  group,
}: {
  group: RelatedContactAttachmentGroup;
}) {
  return (
    <section style={styles.group}>
      <div style={styles.groupTitle}>
        Chat · {formatDate(group.initiatedAt)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {group.attachments.map((attachment) => (
          <AttachmentRow
            key={`${group.contactId}:${attachment.attachmentId}`}
            attachment={attachment}
          />
        ))}
      </div>
    </section>
  );
}

export function AttachmentsPanel({ contactId }: { contactId: string }) {
  const [groups, setGroups] = useState<RelatedContactAttachmentGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setGroups([]);
    setLoading(true);
    setError(null);

    getRelatedContactAttachments()
      .then((result) => {
        if (!cancelled) setGroups(result.contacts);
      })
      .catch((err) => {
        if (!cancelled) {
          setGroups([]);
          setError(err instanceof Error ? err.message : "Unable to load attachments");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contactId, reloadToken]);

  const attachmentCount = groups.reduce(
    (total, group) => total + group.attachments.length,
    0,
  );

  if (!isExpanded) {
    return (
      <aside
        style={{ ...styles.panel, ...styles.collapsedPanel }}
        aria-label={`Attachments (${attachmentCount})`}
      >
        <div style={styles.collapsedContent}>
          <button
            type="button"
            style={styles.iconButton}
            onClick={() => setIsExpanded(true)}
            aria-label="Expand attachments panel"
            title="Expand attachments"
          >
            <ChevronLeft aria-hidden size={18} />
          </button>
          <Paperclip aria-hidden size={17} color="#5f6b7a" />
          <span style={styles.countBadge}>{attachmentCount}</span>
        </div>
      </aside>
    );
  }

  return (
    <aside style={styles.panel} aria-label="Attachments">
      <div style={styles.header}>
        <div style={styles.heading}>
          <Paperclip aria-hidden size={17} />
          <span>Attachments</span>
          <span style={styles.countBadge}>{attachmentCount}</span>
        </div>
        <button
          type="button"
          style={styles.iconButton}
          onClick={() => setIsExpanded(false)}
          aria-label="Collapse attachments panel"
          title="Collapse attachments"
        >
          <ChevronRight aria-hidden size={18} />
        </button>
      </div>
      <div style={styles.scrollArea}>
        {loading ? (
          <div style={styles.state}>Loading attachments…</div>
        ) : error ? (
          <div style={{ ...styles.state, ...styles.error }}>
            <span>{error}</span>
            <button
              type="button"
              style={styles.button}
              onClick={() => setReloadToken((value) => value + 1)}
            >
              <RefreshCw aria-hidden size={14} />
              Retry
            </button>
          </div>
        ) : groups.length === 0 ? (
          <div style={styles.state}>
            <Paperclip aria-hidden size={24} color="#94a3b8" />
            No attachments were found on related contacts.
          </div>
        ) : (
          groups.map((group) => (
            <AttachmentGroup key={group.contactId} group={group} />
          ))
        )}
      </div>
    </aside>
  );
}
